<template>
  <div class="smart-alerts">
    <!-- Loading -->
    <div v-if="loading" class="sa-skeleton">
      <div v-for="n in 3" :key="n" class="sa-skeleton-row">
        <div class="skeleton-dot"></div>
        <div class="skeleton-lines">
          <div class="skeleton-line w80"></div>
          <div class="skeleton-line w60"></div>
        </div>
      </div>
    </div>

    <!-- Teaser list (max 3) -->
    <div v-else-if="teaserItems.length" class="sa-list">
      <div
        v-for="item in teaserItems"
        :key="item.id || item.type"
        class="sa-item"
      >
        <span :class="['sa-dot', item.severity]"></span>
        <span class="sa-alert-title">{{ item.title }}</span>
        <span :class="['sa-tag', 'tag-' + item.severity]">{{
          severityLabel(item.severity)
        }}</span>
      </div>
    </div>

    <!-- Empty -->
    <div v-else class="sa-empty">
      <i class="el-icon-circle-check"></i>
      <span>Todo al dia</span>
    </div>

    <!-- Footer — abre drawer -->
    <div v-if="teaserItems.length" class="sa-footer">
      <button class="sa-footer-btn" @click="openDrawer">
        Ver y gestionar en notificaciones
        <i class="el-icon-arrow-right"></i>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "SmartAlerts",
  computed: {
    loading() {
      return this.$store.state.notificationsStore.loading;
    },
    items() {
      return this.$store.state.notificationsStore.items;
    },
    unreadCount() {
      return this.$store.state.notificationsStore.unreadCount;
    },
    teaserItems() {
      // Max 3, prioritize urgent
      return this.items.slice(0, 3);
    }
  },
  mounted() {
    // Fetch if not already loaded
    if (!this.items.length) {
      this.$store.dispatch("notificationsStore/fetchNotifications");
    }
  },
  methods: {
    severityLabel(severity) {
      const map = {
        urgent: "Urgente",
        warning: "Atencion",
        opportunity: "Oportunidad",
        info: "Info"
      };
      return map[severity] || severity;
    },
    openDrawer() {
      this.$store.dispatch("notificationsStore/openDrawer");
    }
  }
};
</script>

<style lang="scss" scoped>
$primary: #31355c;
$accent: #3fdbbf;
$border: #e4eaf6;
$bg: #f7f8f9;

.smart-alerts {
  // No background/border — parent bi-card provides it
}

.sa-list {
  display: flex;
  flex-direction: column;
}
.sa-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 0;
  border-bottom: 1px solid $border;
  &:last-child {
    border-bottom: none;
  }
}
.sa-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  &.urgent {
    background: #e24b4a;
  }
  &.warning {
    background: #ba7517;
  }
  &.opportunity {
    background: #1d9e75;
  }
  &.info {
    background: #534ab7;
  }
}
.sa-alert-title {
  font-size: 13px;
  font-weight: 600;
  color: $primary;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sa-tag {
  font-size: 9px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  flex-shrink: 0;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  &.tag-urgent {
    background: #fcebeb;
    color: #a32d2d;
  }
  &.tag-warning {
    background: #faeeda;
    color: #854f0b;
  }
  &.tag-opportunity {
    background: #e1f5ee;
    color: #0f6e56;
  }
  &.tag-info {
    background: #eeedfe;
    color: #3c3489;
  }
}

.sa-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  color: #3fdbbf;
  gap: 8px;
  i {
    font-size: 32px;
  }
  span {
    font-size: 13px;
    font-weight: 600;
    color: #909399;
  }
}

.sa-footer {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid $border;
}
.sa-footer-btn {
  width: 100%;
  padding: 8px;
  font-size: 12px;
  font-weight: 600;
  color: $accent;
  background: transparent;
  border: 1px solid $border;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.15s;
  &:hover {
    background: $bg;
    border-color: $accent;
  }
}

// Skeleton
.sa-skeleton {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.sa-skeleton-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.skeleton-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(90deg, #f0f2f7 25%, #e8ecf5 50%, #f0f2f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  flex-shrink: 0;
  margin-top: 4px;
}
.skeleton-lines {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.skeleton-line {
  height: 10px;
  background: linear-gradient(90deg, #f0f2f7 25%, #e8ecf5 50%, #f0f2f7 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  &.w60 {
    width: 60%;
  }
  &.w80 {
    width: 80%;
  }
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
